/**
 * Rapports de cotisations par période (mensuel / trimestriel / semestriel / annuel).
 * Filtre sur la clé mois YYYY-MM des cotisations régulières.
 */

export const PERIOD_TYPES = ['monthly', 'quarterly', 'semiannual', 'annual'];

function pad2(n) {
  return String(n).padStart(2, '0');
}

/** @returns {{ startYm: string, endYm: string, labelKey: string, labelParams: object } | null} */
export function resolveContributionPeriodRange(periodType, { year, month, quarter, half } = {}) {
  const y = Number(year);
  if (!Number.isFinite(y) || y < 2000 || y > 2100) return null;
  const type = String(periodType || '').toLowerCase();

  if (type === 'monthly') {
    const m = Number(month);
    if (!Number.isFinite(m) || m < 1 || m > 12) return null;
    const ym = `${y}-${pad2(m)}`;
    return {
      startYm: ym,
      endYm: ym,
      labelKey: 'monthly',
      labelParams: { year: y, month: m },
    };
  }

  if (type === 'quarterly') {
    const q = Number(quarter);
    if (!Number.isFinite(q) || q < 1 || q > 4) return null;
    const startM = (q - 1) * 3 + 1;
    const endM = startM + 2;
    return {
      startYm: `${y}-${pad2(startM)}`,
      endYm: `${y}-${pad2(endM)}`,
      labelKey: 'quarterly',
      labelParams: { year: y, quarter: q },
    };
  }

  if (type === 'semiannual') {
    const h = Number(half);
    if (h !== 1 && h !== 2) return null;
    const startM = h === 1 ? 1 : 7;
    const endM = h === 1 ? 6 : 12;
    return {
      startYm: `${y}-${pad2(startM)}`,
      endYm: `${y}-${pad2(endM)}`,
      labelKey: 'semiannual',
      labelParams: { year: y, half: h },
    };
  }

  if (type === 'annual') {
    return {
      startYm: `${y}-01`,
      endYm: `${y}-12`,
      labelKey: 'annual',
      labelParams: { year: y },
    };
  }

  return null;
}

export function contributionMonthKey(contrib) {
  const raw = String(contrib?.month || '').trim();
  if (/^\d{4}-\d{2}/.test(raw)) return raw.slice(0, 7);
  return '';
}

export function isMonthKeyInRange(ym, startYm, endYm) {
  if (!ym || !startYm || !endYm) return false;
  return ym >= startYm && ym <= endYm;
}

function statusBucket(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'completed' || s === 'approved' || s === 'paid') return 'completed';
  if (s === 'rejected') return 'rejected';
  if (s === 'pending' || s === 'unpaid' || s === 'awaiting' || s === 'draft') return 'pending';
  return 'other';
}

/**
 * @param {object[]} contributions
 * @param {{ startYm: string, endYm: string }} range
 * @param {{ personId?: string, getMemberId?: (c: object) => string, getMemberName?: (c: object) => string }} [opts]
 */
export function buildContributionPeriodReport(contributions, range, opts = {}) {
  const startYm = String(range?.startYm || '');
  const endYm = String(range?.endYm || '');
  const personFilter = String(opts.personId || '').trim();
  const getMemberId =
    typeof opts.getMemberId === 'function'
      ? opts.getMemberId
      : (c) => String(c?.person_id || c?.member_id || '').trim();
  const getMemberName =
    typeof opts.getMemberName === 'function'
      ? opts.getMemberName
      : (c) => String(c?.member_name || c?.person_name || getMemberId(c) || '—').trim();

  const rows = [];
  for (const c of Array.isArray(contributions) ? contributions : []) {
    const ym = contributionMonthKey(c);
    if (!ym || !isMonthKeyInRange(ym, startYm, endYm)) continue;
    const memberId = getMemberId(c);
    if (personFilter && memberId !== personFilter) continue;
    const bucket = statusBucket(c?.status);
    const amount = Number(c?.amount || 0);
    rows.push({
      id: c?.id,
      memberId,
      memberName: getMemberName(c),
      month: ym,
      amount,
      status: bucket,
      statusRaw: String(c?.status || ''),
      paymentMethod: String(c?.payment_method || ''),
      notes: String(c?.notes || ''),
      paymentDate: String(c?.payment_date || c?.created_at || '').slice(0, 10),
    });
  }

  rows.sort((a, b) => {
    const byMonth = String(b.month).localeCompare(String(a.month));
    if (byMonth) return byMonth;
    return String(a.memberName).localeCompare(String(b.memberName), 'fr');
  });

  const summary = {
    count: rows.length,
    totalAmount: 0,
    completedCount: 0,
    completedAmount: 0,
    pendingCount: 0,
    pendingAmount: 0,
    rejectedCount: 0,
    rejectedAmount: 0,
    membersCount: new Set(rows.map((r) => r.memberId).filter(Boolean)).size,
  };

  for (const r of rows) {
    summary.totalAmount += r.amount || 0;
    if (r.status === 'completed') {
      summary.completedCount += 1;
      summary.completedAmount += r.amount || 0;
    } else if (r.status === 'pending') {
      summary.pendingCount += 1;
      summary.pendingAmount += r.amount || 0;
    } else if (r.status === 'rejected') {
      summary.rejectedCount += 1;
      summary.rejectedAmount += r.amount || 0;
    }
  }

  return { rows, summary, startYm, endYm };
}

function csvEscape(value) {
  const s = String(value ?? '');
  if (s.includes('"') || s.includes(',') || s.includes('\n')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

export function buildContributionPeriodReportCsv(report, { orgName = '', periodLabel = '', localeFmt = 'fr-FR' } = {}) {
  const headers = [
    'member',
    'member_id',
    'month',
    'amount_fbu',
    'status',
    'payment_method',
    'payment_date',
    'notes',
  ];
  const lines = [
    `# ${orgName || 'Entrea'} — ${periodLabel}`.trim(),
    `# ${report.startYm} → ${report.endYm}`,
    `# total=${Number(report.summary.totalAmount || 0).toLocaleString(localeFmt)} completed=${report.summary.completedCount} pending=${report.summary.pendingCount}`,
    headers.join(','),
  ];
  for (const r of report.rows) {
    lines.push(
      [
        csvEscape(r.memberName),
        csvEscape(r.memberId),
        csvEscape(r.month),
        csvEscape(String(Math.round(Number(r.amount || 0)))),
        csvEscape(r.status),
        csvEscape(r.paymentMethod),
        csvEscape(r.paymentDate),
        csvEscape(r.notes),
      ].join(',')
    );
  }
  return `${lines.join('\n')}\n`;
}

/** Valeurs par défaut : mois / année courants. */
export function defaultPeriodSelection(now = new Date()) {
  return {
    periodType: 'monthly',
    year: now.getFullYear(),
    month: now.getMonth() + 1,
    quarter: Math.floor(now.getMonth() / 3) + 1,
    half: now.getMonth() < 6 ? 1 : 2,
  };
}
